/* Auth: explicit anonymous or account auth, with anonymous upgrades.
 *
 * - The entry screen lets people choose an anonymous session or an account.
 * - Accounts created while anonymous link onto the existing user so progress
 *   remains attached to the same uid.
 * - "Sign in with Google" LINKS the Google credential onto the anonymous
 *   user — same uid, all progress kept, no migration. If that Google
 *   account already exists (was linked before), we sign into it instead and
 *   the Learn-state merge (learn/sync.js) reconciles local progress.
 * - Everything degrades gracefully to local-only when Firebase isn't
 *   configured or a provider is disabled: callers just see user === null.
 */
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { auth, googleProvider } from "./firebase.js";
import { isNative } from "./native.js";
import { ownedByAnotherAccount, recordOwner, wipeAccountData } from "./deviceData.js";

let current = null;
import { captureAttribution } from "./attribution.js";
import { saveAcquisition } from "./saveAcquisition.js";

let ready = false; // first onAuthStateChanged fired
const listeners = new Set();

function emit() {
  for (const l of listeners) l({ user: current, ready });
}

/* Capture how this visitor arrived before anything can discard the query
 * string — the Google popup/redirect round-trip does exactly that, so reading
 * it later would always report "direct". */
captureAttribution();

function adopt(user) {
  recordOwner(user);
  current = user;
  ready = true;
  emit();
  /* First-touch acquisition, recorded once per account. Deliberately not
   * awaited: signing in must not wait on a metrics write, and a failure here
   * is logged rather than surfaced. */
  if (user?.uid) void saveAcquisition(user.uid);
}

if (auth) {
  onAuthStateChanged(auth, (user) => {
    /* The device still holds another real account's data (see deviceData.js:
     * a session that ended without signOutUser, or a wipe cut short). Every
     * listener would merge it into THIS user's cloud copy the moment it heard
     * about them, so nobody hears yet: clear it, then start over from a clean
     * page. Synchronous otherwise on purpose — an await on the common path
     * would let a later auth event overtake this one. */
    if (user && ownedByAnotherAccount(user)) {
      wipeAccountData()
        .catch(() => {})
        .finally(() => {
          recordOwner(user);
          // A storage that refuses the new owner would reload forever; carry on instead.
          if (ownedByAnotherAccount(user)) adopt(user);
          else window.location.reload();
        });
      return;
    }
    adopt(user);
  });
} else {
  ready = true;
}

export function subscribeAuth(cb) {
  listeners.add(cb);
  cb({ user: current, ready });
  return () => listeners.delete(cb);
}

/* The signed-in user right now, for the handful of callers that need one
 * inside a callback rather than as React state (the recap's mail queue is the
 * first). Null before auth resolves, and for a guest. */
export const currentUser = () => current || null;

export async function continueAnonymously() {
  if (!auth) return {};
  try {
    await signInAnonymously(auth);
    return {};
  } catch (e) {
    return { error: e.code || String(e) };
  }
}

export async function signInWithGoogle() {
  if (!auth) return { error: "firebase-not-configured" };
  return isNative ? signInWithGoogleNative() : signInWithGoogleWeb();
}

/* Native shells (Android/iOS): OAuth popups don't work inside a WebView, so
 * the Capacitor plugin runs the platform's own Google sign-in UI (with
 * skipNativeAuth) and hands back the OAuth tokens. Those sign the user into
 * the SAME Firebase JS SDK the whole app uses — linking, Firestore sync,
 * onAuthStateChanged all behave exactly as on the web. */
async function signInWithGoogleNative() {
  let oauth;
  try {
    const result = await FirebaseAuthentication.signInWithGoogle();
    oauth = result.credential;
  } catch (e) {
    const text = `${e.code || ""} ${e.message || e}`;
    if (/cancel/i.test(text)) return { cancelled: true };
    return { error: e.code || String(e) };
  }
  if (!oauth?.idToken) return { error: "auth/missing-credential" };
  const credential = GoogleAuthProvider.credential(oauth.idToken, oauth.accessToken);
  try {
    if (auth.currentUser?.isAnonymous) {
      await linkWithCredential(auth.currentUser, credential);
      recordOwner(auth.currentUser);
    } else {
      await signInWithCredential(auth, credential);
    }
    return {};
  } catch (e) {
    if (e.code === "auth/credential-already-in-use" || e.code === "auth/email-already-in-use") {
      // This Google account already has a user: sign into it; the Learn
      // merge (learn/sync.js) reconciles any anonymous progress.
      try {
        await signInWithCredential(auth, credential);
        return {};
      } catch (e2) {
        return { error: e2.code || String(e2) };
      }
    }
    return { error: e.code || String(e) };
  }
}

async function signInWithGoogleWeb() {
  try {
    if (auth.currentUser?.isAnonymous) {
      await linkWithPopup(auth.currentUser, googleProvider);
      recordOwner(auth.currentUser);
    } else {
      await signInWithPopup(auth, googleProvider);
    }
    return {};
  } catch (e) {
    if (e.code === "auth/credential-already-in-use" || e.code === "auth/email-already-in-use") {
      // This Google account already has a user. Reuse the credential from
      // the completed link popup rather than opening a second popup, which
      // browsers commonly block once the original click gesture has ended.
      try {
        const credential = GoogleAuthProvider.credentialFromError(e);
        if (!credential) return { error: e.code || "auth/missing-credential" };
        await signInWithCredential(auth, credential);
        return {};
      } catch (e2) {
        return { error: e2.code || String(e2) };
      }
    }
    if (e.code === "auth/popup-closed-by-user" || e.code === "auth/cancelled-popup-request") {
      return { cancelled: true };
    }
    return { error: e.code || String(e) };
  }
}

export async function signInWithEmail(email, password) {
  if (!auth) return { error: "firebase-not-configured" };
  try {
    await signInWithEmailAndPassword(auth, email, password);
    return {};
  } catch (e) {
    return { error: e.code || String(e) };
  }
}

export async function registerWithEmail(email, password) {
  if (!auth) return { error: "firebase-not-configured" };
  try {
    if (auth.currentUser?.isAnonymous) {
      const credential = EmailAuthProvider.credential(email, password);
      await linkWithCredential(auth.currentUser, credential);
      recordOwner(auth.currentUser);
    } else {
      await createUserWithEmailAndPassword(auth, email, password);
    }
    return {};
  } catch (e) {
    return { error: e.code || String(e) };
  }
}

/* How the signed-in user proves who they are: "password", "google", or
 * "none" for an anonymous session (which has no credential to re-present).
 * Drives which confirmation the delete-account flow asks for. */
export function credentialKind(user = current) {
  if (!user || user.isAnonymous) return "none";
  const ids = user.providerData.map((entry) => entry.providerId);
  if (ids.includes("password")) return "password";
  if (ids.includes("google.com")) return "google";
  return "none";
}

/* Proves the person at the keyboard is the account holder, immediately before
 * something irreversible (account deletion). Firebase also *requires* this when
 * the session is older than a few minutes — asking always, rather than only
 * after catching auth/requires-recent-login, keeps the destructive path down to
 * one code path instead of two.
 *
 * Anonymous sessions can't reauthenticate: there is no credential. They fall
 * through as OK, which is the honest answer — the uid is a device-local
 * throwaway and whoever holds the device already has the data. */
export async function reauthenticate({ password } = {}) {
  const user = auth?.currentUser;
  if (!user) return { error: "auth/no-current-user" };
  const kind = credentialKind(user);
  try {
    if (kind === "password") {
      if (!password) return { error: "auth/missing-password" };
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
    } else if (kind === "google") {
      if (isNative) {
        // Same reason as signInWithGoogleNative: OAuth popups don't work in a
        // WebView, so re-run the platform's sign-in and re-present the token.
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) return { error: "auth/missing-credential" };
        await reauthenticateWithCredential(user, GoogleAuthProvider.credential(idToken, result.credential.accessToken));
      } else {
        await reauthenticateWithPopup(user, googleProvider);
      }
    }
    return {};
  } catch (e) {
    const text = `${e.code || ""} ${e.message || e}`;
    if (/cancel|popup-closed/i.test(text)) return { cancelled: true };
    return { error: e.code || String(e) };
  }
}

/* Closes the Firebase Auth account. Deliberately does NOT touch Firestore —
 * accountDeletion.js owns the order, and that order matters (the data must go
 * first, while the rules still recognise this uid). */
export async function deleteAuthUser() {
  const user = auth?.currentUser;
  if (!user) return { error: "auth/no-current-user" };
  try {
    await deleteUser(user);
    // Only after the JS SDK is done with this user: clearing the native Google
    // session first can leave the plugin and the JS SDK disagreeing about who
    // is signed in, and deleteUser needs a live token.
    if (isNative) await FirebaseAuthentication.signOut().catch(() => {});
    return {};
  } catch (e) {
    return { error: e.code || String(e) };
  }
}

/* Signing out hands the device back: the account's progress lives on in its
 * cloud copy, and nothing of it may stay here for whoever signs in next (see
 * deviceData.js for what that used to do). Order matters:
 *   1. land the debounced Learn write while this uid can still write — the
 *      wait is bounded, because offline a setDoc never settles;
 *   2. sign out, so nothing still running can sync this account any further;
 *   3. wipe the local copies;
 *   4. reload, which drops the in-memory ones too. */
export async function signOutUser() {
  if (!auth) return;
  try {
    const { flushRemoteLearn } = await import("./learn/sync.js");
    await Promise.race([flushRemoteLearn(), new Promise((resolve) => setTimeout(resolve, 4000))]);
  } catch {
    /* the Learn chunk can't load (offline) — then it never loaded, and held nothing to flush */
  }
  if (isNative) {
    // Also clears the native Google session so the account picker shows
    // again on the next sign-in instead of silently reusing the account.
    await FirebaseAuthentication.signOut().catch(() => {});
  }
  await signOut(auth);
  await wipeAccountData().catch(() => {});
  window.location.replace("/");
}
